import { Box, Paper, Typography } from "@mui/material";
import { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ColumnVisibilityButton from "../../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../../components/button/RefreshButton";
import { TableHeaderContainer } from "../../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../../components/container/TableTitleContainer";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import AgGrid from "../../../components/grid/AgGrid";
import { defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import BuildingProductDropdown from "./BuildingProductDropdown";

// Building-only variant of MaterialCompositionTable (../../Products/Circularity/MaterialCompositionTable.js).
// Renders the shared BuildingProductDropdown as a *controlled* component — the selection,
// the processes list, and the WBLCA fetch all live in BuildingCircularity.js (shared with
// the Utility and MCI Calculation tabs so switching tabs keeps the same product selected
// and only one fetch happens, not one per tab). `combined_data` here is already scoped to
// the selected product by the parent.
const BuildingMaterialCompositionTable = ({ combined_data, activeTab, tabValue, selectedSubcategoryId, onSubcategoryChange }) => {
  const { t } = useTranslation();
  const { columnVisibilities } = useContextProvider();
  const colVisibility_key = "material_composition_table_column_visibility";
  const gridHeaderContainerRef = useRef(null);
  const materials = useMemo(() => combined_data.combined_circularities, [combined_data]);
  const [rows, setRows] = useState([]);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);

  // Materials with no real mass (_massIsNA) are shown as N/A, not 0 — exclude them here too
  // so the total doesn't silently include calculate_mass's quantity-derived fallback estimate.
  const massSum = useMemo(() => materials.reduce((s, m) => s + (m._massIsNA ? 0 : (m.mass || 0)), 0), [materials]);

  useEffect(() => {
    if (activeTab && tabValue && activeTab !== tabValue) {
      setColumnMenuVisible(false);
    }
  }, [activeTab, tabValue]);

  useEffect(() => {
    setRows(
      materials.map((material) => ({
        ...material,
        renewablePct: material.renewablePct ?? 0,
        source: material.source ?? "ArivuDB",
      }))
    );
  }, [materials]);

  const onCellValueChanged = (params) => {
    const value = params.value;
    const parsed = parseFloat(value);
    const clamped = isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === params.data.id ? { ...row, renewablePct: clamped } : row
      )
    );
  };

  const colDefs = useMemo(() => {
    const isHidden = (field) => {
      if (field === "si_no") return false;
      return !columnVisibilities[colVisibility_key]?.includes(field);
    };
    return [
      {
        headerName: t("SI No."),
        valueGetter: (params) => {
          if (params.node.rowPinned === "bottom") return "";
          return params.node.rowIndex + 1;
        },
        width: 80,
        minWidth: 60,
        headerClass: "ag-center-aligned-header",
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
        suppressMenu: true,
        sortable: false,
        field: "si_no"
      },
      { headerName: "Type", field: "type", ...leftAlignColDef },
      { headerName: "Category", field: "category", ...leftAlignColDef },
      { headerName: "Sub Category", field: "sub_category", ...leftAlignColDef },
      { headerName: "Description", field: "description", ...leftAlignColDef, flex: 1 },
      {
        headerName: "Mass (kg)", field: "mass", ...rightAlignColDef,
        // _massIsNA (set while merging the WBLCA fetch in BuildingCircularity.js) reflects
        // whether the source actually had a mass value — params.value itself is never null
        // since calculate_mass always fills in a computed number.
        valueFormatter: (params) => (params.data?._massIsNA
          ? t("N/A")
          : floatFormater.valueFormatter(params)),
      },
      { headerName: "Normalized Mass (%)", field: "normalized_mass", ...rightAlignColDef, ...floatFormater },
      { headerName: "GWP (kg CO₂e)", field: "gwp_total", ...rightAlignColDef, ...floatFormater },
      { headerName: "Normalized GWP", field: "normalized_gwp", ...rightAlignColDef, ...floatFormater },
      { headerName: t("Renewable Content (%)"), field: "renewablePct", ...rightAlignColDef, ...floatFormater, editable: true, minWidth: 180 },
      { headerName: t("Data Source"), field: "source", ...leftAlignColDef, minWidth: 150 },
    ].map((col) => ({ ...defaultColDef, ...col, hide: isHidden(col.field) }));
  }, [columnVisibilities, colVisibility_key, t]);
  const defaultCols = colDefs.map((col) => col.field);

  return (
    <Paper sx={{ border: 1, borderColor: "#e0e0e0", borderRadius: 4, overflow: "hidden" }}>
      <TableHeaderContainer ref={gridHeaderContainerRef}>
        <TableTitleContainer>
          <Typography variant="Table Title">{t("Material Composition")}</Typography>
        </TableTitleContainer>
        <TableHeaderToolsContainer>
          {onSubcategoryChange && (
            <BuildingProductDropdown value={selectedSubcategoryId} onChange={onSubcategoryChange} />
          )}
          <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
          <RefreshButton />
        </TableHeaderToolsContainer>
      </TableHeaderContainer>

      <Box sx={{ height: "auto" }}>
        <AgGrid
          columnDefs={colDefs}
          rowData={rows}
          onCellValueChanged={onCellValueChanged}
          headerCheckbox={false}
          footerLeftContent={
            <Typography variant="List Title" sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary" }}>
              {t("Total Mass")} (kg): {massSum.toFixed(2)}
            </Typography>
          }
        />
      </Box>

      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
    </Paper>
  );
};
export default BuildingMaterialCompositionTable;
